import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { delegate } from "tippy.js";
import { VoteWindowInterface } from "@/appInterface/windows/VoteWindow/interface";
import type { VoteEntity } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import { canv } from "@/canv/global";
import { isOnScreen } from "@/canv/utils";
import { eventEmitter } from "@/eventEmitter";
import type { Group } from "@/group";
import { isItemWrapperGroup } from "@/utils";
import { StartVoteWindow, type StartVoteWindowVote } from "../StartVoteWindow";
import type { ActiveTab, NewVoteState, Vote } from "./types";
import { createVoteBuildEvent, getDefaultNewVoteState } from "./utils";
import { VoteButtonTimer } from "./VoteButtonTimer";
import { VoteScreen } from "./VoteScreen";
import style from "./VoteWindow.module.css";
import { VoteWindowLayout } from "./VoteWindowLayout";

export class VoteWindow {
    constructor() {
        this.screen = new VoteScreen();
        this.interface = new VoteWindowInterface(this);
    }

    updateTimer() {
        const activeVote = canv.canvasController.activeVote as unknown as Vote | null;
        if (!this.timerRoot || !activeVote) return;
        this.timerRoot.render(
            React.createElement(VoteButtonTimer, {
                startedAt: activeVote.start,
                duration: activeVote.duration,
                onUnmount: this.resetTimer,
            }),
        );
    }

    showStartVoteWindow(vote: StartVoteWindowVote) {
        if (this.startModal) return;

        const startModalDiv = document.createElement("div");
        startModalDiv.id = "startVoteWindow";

        this.startModal = createRoot(startModalDiv);
        this.startModal.render(
            React.createElement(StartVoteWindow, {
                onClose: () => {
                    this.startModal?.unmount();
                    this.startModal = null;
                    document.body.removeChild(startModalDiv);
                },
                onAccept: () => {
                    if (canv.canvasController.activeVote) {
                        canv.appInterface.activeVote.open();
                    }
                    this.close(false);
                },
                vote,
            }),
        );

        document.body.appendChild(startModalDiv);
    }

    open() {
        if (this.opened) return;

        const voteWindowWrapper = document.createElement("div");
        voteWindowWrapper.classList.add(style.voteWindowWrapper);

        const bottomBarWrapper = document.querySelector("#bottom-bar-wrapper-react");

        if (!bottomBarWrapper) {
            throw new Error("Cannot find module 'bottomBarWrapper'");
        }

        this.voteWindowWrapper = voteWindowWrapper;
        bottomBarWrapper.append(voteWindowWrapper);

        this.root = createRoot(voteWindowWrapper);
        this.render();

        this.opened = true;

        eventEmitter.dispatch("header.button.active.name", {
            name: "vote",
        });
        eventEmitter.dispatch("vote.start.visibility", {
            isVisible: true,
        });
    }

    render() {
        const VoteWindowLayoutElement = React.createElement(VoteWindowLayout, {
            onClose: this.close,
            activeTab: this.activeTab,
            onChangeTab: this.changeTab,
            newVoteState: this.newVoteState,
            onUpdate: this.onUpdateNewVoteState,
            onStart: this.handleStart,
            onMount: this.mount,
            isShowResult: this.isShowResult,
            voteResult: this.voteResult,
            setShowResult: (val: boolean, vote?: VoteEntity) => {
                this.isShowResult = val;
                if (vote) {
                    this.voteResult = vote;
                    this.widgetUIDs = new Set(
                        Object.values(vote.children).map((child) => child.linkToOriginalUID),
                    );
                }
                this.render();
            },
            selectResultItem: this.selectResultItem,
            selectedResultItemUID: this.selectedResultItemUID,
            isGuest: canv.isGuest(),
        });

        if (this.root) {
            this.root.render(VoteWindowLayoutElement);
        } else {
            throw new Error("VoteWindow can't find this.root");
        }

        delegate("body", {
            target: ".disabledWidgetCheckbox",
            content: "В выделенной области нет подходящих объектов",
            placement: "bottom",
        });
    }

    selectResultItem = (UID: string) => {
        this.selectedResultItemUID = UID;
        let group: Group | undefined;
        for (const item of canv.canvasController.items) {
            if (isItemWrapperGroup(item)) {
                if (item.children[0]?.UID === UID) {
                    group = item;
                    break;
                }
            }
        }

        if (!group) return;

        if (canv.zoomProcent < 100 || !isOnScreen(group)) {
            canv.zoomToVoteResultItem(group as unknown as Group[]);
        }
        this.render();
    };

    mount = (element: HTMLElement) => {
        this.voteWindowElement = element;
        this.showScreen(element);
    };

    showScreen(element: HTMLElement) {
        if (this.activeTab === "new") {
            this.screen?.create(element);
        } else {
            this.screen?.remove();
        }
    }

    changeTab = (activeTab: ActiveTab) => {
        this.activeTab = activeTab;

        eventEmitter.dispatch("vote.start.activeTab", { tab: activeTab });

        if (this.voteWindowElement) {
            this.showScreen(this.voteWindowElement);
        }
        this.render();
    };

    toggle() {
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    close = (shouldToggleSidebarVisibility = true) => {
        if (!this.opened) {
            return;
        }
        this.newVoteState = getDefaultNewVoteState();
        this.widgetUIDs.clear();
        this.root?.unmount();
        this.screen?.remove();
        this.opened = false;
        this.isShowResult = false;
        this.activeTab = "new";

        eventEmitter.dispatch("vote.start.activeTab", {
            tab: "new",
        });

        if (this.voteWindowWrapper?.parentNode) {
            this.voteWindowWrapper.parentNode.removeChild(this.voteWindowWrapper);
            this.voteWindowWrapper = null;
        }

        if (shouldToggleSidebarVisibility) {
            eventEmitter.dispatch("header.button.active.name", {
                name: null,
            });
        }
        eventEmitter.dispatch("vote.start.visibility", {
            isVisible: false,
        });
    };

    onUpdateNewVoteState = <T extends keyof NewVoteState>(key: T, value: NewVoteState[T]) => {
        if (typeof value === "number" && (value > 99 || value <= 0)) return;
        this.newVoteState[key] = value;

        this.render();
    };

    handleStart = () => {
        canv.intercom.eventMessage(createVoteBuildEvent(this.newVoteState));
        this.newVoteState = getDefaultNewVoteState();
        this.screen?.remove();
        this.widgetUIDs.clear();
        this.close(false);
    };

    updateWidgetList(selectedGroupUIDs: string[] = [], candidates: Group[] = []) {
        const newWidgetsList = getDefaultNewVoteState().widgetsList;

        for (const candidate of candidates) {
            const constructorName = candidate.onlyChild()?.constructorName;
            const foundItemIndex = newWidgetsList.findIndex(
                (item) => item.value === constructorName,
            );

            if (foundItemIndex > -1) {
                newWidgetsList[foundItemIndex].candidateCount += 1;

                if (selectedGroupUIDs.includes(candidate.UID)) {
                    newWidgetsList[foundItemIndex].selectedCount += 1;
                }
            }
        }

        this.onUpdateNewVoteState("widgetsList", newWidgetsList);
    }

    updatePastVoteList() {
        if (this.activeTab === "past" && !this.isShowResult) {
            this.render();
            this.rerenderTrigger = !this.rerenderTrigger;
        }
    }

    resetTimer = () => {
        eventEmitter.dispatch("vote.timer.visibility", { isVisible: false });

        if (!this.opened) {
            eventEmitter.dispatch("header.button.active.name", { name: null });
        }
    };

    showResultState() {
        this.resetTimer();
        this.activeTab = "past";
        eventEmitter.dispatch("vote.start.activeTab", { tab: "past" });
        if (canv.canvasController.pastVotes.length > 0) {
            this.voteResult =
                canv.canvasController.pastVotes[canv.canvasController.pastVotes.length - 1];
            if (this.voteResult) {
                this.widgetUIDs = new Set(
                    Object.values(this.voteResult).map((child) => child.linkToOriginalUID),
                );
            }
            this.isShowResult = true;
        }
        this.open();
    }

    root: Root | null = null;
    opened = false;
    activeTab: ActiveTab = "new";
    screen: VoteScreen | null = null;
    infoModal: Root | null = null;
    startModal: Root | null = null;
    timerRoot: Root | null = null;
    isShowResult = false;
    voteResult: VoteEntity | null = null;
    selectedResultItemUID = "";
    widgetUIDs: Set<string> = new Set();

    newVoteState: NewVoteState = getDefaultNewVoteState();
    voteWindowElement: HTMLElement | null = null;
    voteWindowWrapper: HTMLElement | null = null;
    interface: VoteWindowInterface;
    rerenderTrigger = false;
}
